import { z } from 'zod';

/**
 * The magic-link payload.
 *
 * The `:token` path segment is base64 of a URL-encoded JSON object, minted by
 * the agent's email. It arrives from outside the app and is fully attacker-
 * controllable, so it is parsed rather than cast — waive-dp typed the decoded
 * blob as `any` and read fields straight off it, which turns a malformed link
 * into an undefined creeping into the login request body.
 *
 * It is validated, but only as strictly as the login call genuinely requires:
 * a link a director cannot influence should not be refused over a field the
 * request never sends.
 */

/**
 * Ids arrive from an ASIC feed that is not consistent about their type: the
 * same field is a JSON number in some links and a string in others. Coercing
 * beats rejecting — a director cannot do anything about how their link was
 * minted, and "invalid link" for a link that works elsewhere is the worst
 * possible outcome.
 */
const linkId = z.union([z.string(), z.number()]).transform(String);

export const linkPayloadSchema = z.object({
  /** Single-use bearer for `POST /director/login`; not the session token. The
   *  one field with no sensible fallback — without it the call cannot be made. */
  authToken: z.string().min(1),
  asicMemberId: linkId,
  /** Optional: some links (a form sent to a director across companies) carry
   *  no company at all. */
  asicCompanyId: linkId.nullish(),
  documentsId: linkId.nullish(),
  /** Given name. Named `name1` by the ASIC feed the link is generated from. */
  name1: z.string().nullish(),
  familyName: z.string().nullish(),
  hashCode: z.string().nullish(),
  /** Left open rather than an enum: an unrecognised type falls through to the
   *  dashboard, which is a working screen. An enum would reject the whole link. */
  documentType: z.string().nullish(),
  changeId: linkId.nullish(),
  annualReviewId: linkId.nullish(),
  formId: linkId.nullish(),
});

export type LinkPayload = z.infer<typeof linkPayloadSchema>;

/** `POST /director/login`. A 200 without an access token is a failed login —
 *  the API signals rejection in the body, not the status. */
export const loginResponseSchema = z.object({
  accessToken: z.string().min(1),
  refreshToken: z.string().default(''),
  /** The director must clear an OTP before the session counts as authenticated. */
  director2FA: z.boolean().default(false),
  /** Too many failed attempts; the portal is closed to them. */
  dPLocked: z.boolean().default(false),
  otpExpiry: z.number().default(0),
});

export type LoginResponse = z.infer<typeof loginResponseSchema>;

/** Seconds until the server accepts another send. Zero or garbage counts as
 *  unstated, as it did in the legacy portal, and the screen's default applies. */
const retryWindow = z.number().positive().nullish().catch(null);

/**
 * `PUT /director/requestOtp` — both the send on arriving at `/otp` and every
 * resend. The legacy portal made this call as soon as its OTP screen mounted,
 * and nothing earlier in the link flow asks for a code, so skipping it leaves
 * the director waiting for an SMS that was never requested.
 */
export const requestOtpResponseSchema = z.object({
  /** The director cleared OTP within the last 24 hours. No code was sent, and
   *  none is needed — the session is already good. */
  otpVerified: z.boolean().default(false),
  retryAfterSec: retryWindow,
  message: z.string().nullish(),
});

export type RequestOtpResponse = z.infer<typeof requestOtpResponseSchema>;

/** `PUT /director/verifyOtp`. A wrong code can come back as a 200 with
 *  `success: false`, so the status alone does not mean the code was accepted. */
export const verifyOtpResponseSchema = z.object({
  success: z.boolean(),
  error: z.string().nullish(),
});

export type VerifyOtpResponse = z.infer<typeof verifyOtpResponseSchema>;

/**
 * The failure body of either OTP endpoint. The send throttle and the verify
 * lockout report through different fields, so every one the legacy portal read
 * is modelled; all are optional because any may be absent.
 */
export const otpErrorBodySchema = z.object({
  message: z.string().nullish(),
  error: z.string().nullish(),
  details: z.object({ retryAfterSec: retryWindow }).nullish(),
  codeSpecificData: z
    .object({
      retryAfterSec: retryWindow,
      agentEmail: z.string().nullish(),
      agentPhone: z.string().nullish(),
    })
    .nullish(),
});

/** `PUT /auth/refresh`. */
export const refreshResponseSchema = z.object({
  accessToken: z.string().min(1),
  refreshToken: z.string().default(''),
});

/**
 * `GET /director/general` — the signed-in director's own record.
 *
 * There is no `/me` endpoint on this API: the director IS the ASIC member the
 * session was opened for, and this is the record that carries their name. The
 * response also describes their company and agents; only the fields the portal
 * displays are modelled here, and unknown keys are ignored rather than rejected.
 */
/** A residential address as the ASIC feed returns it. Every part is optional
 *  in practice, so the formatter — not the schema — decides what a partial
 *  address looks like on screen. */
export const directorAddressSchema = z.object({
  suite: z.string().nullish(),
  street: z.string().nullish(),
  suburb: z.string().nullish(),
  state: z.string().nullish(),
  postalCode: z.string().nullish(),
  country: z.string().nullish(),
  additionalDetails: z.string().nullish(),
});

export type DirectorAddress = z.infer<typeof directorAddressSchema>;

/**
 * The registered agent managing this director's companies.
 *
 * The portal is white-labelled: it is served by whichever agent holds the
 * director's companies, and wears that agent's logo rather than Waive's. The
 * two payment flags are not cosmetic — they decide who absorbs the merchant fee
 * and whether an SMSF may pay by card, so they change what a director is
 * charged.
 *
 * Every field is tolerant. This record is a *branding* detail hanging off the
 * response that carries the director's own name, and a strict field here would
 * reject that whole response — costing the director their name and address to
 * save a logo.
 */
export const agentSchema = z.object({
  id: z.string().default(''),
  name: z.string().default(''),
  email: z.string().default(''),
  phoneNumber: z.string().default(''),
  /** Absent for an agent that never uploaded one; the header falls back to the
   *  portal's own mark rather than rendering a broken image. */
  logoURL: z.string().nullish(),
  companyId: z.string().nullish(),
  /** The feed sends these as real booleans on some records and as the STRINGS
   *  'true'/'false' on others. `Boolean('false')` is `true`, so the string case
   *  is COMPARED rather than coerced — coercing it would silently tell the
   *  portal the agent absorbs the merchant fee when they do not. */
  agentToPayMerchantFee: z
    .union([z.string(), z.boolean()])
    .nullish()
    .transform((value) => {
      if (typeof value === 'boolean') return value;
      if (typeof value === 'string') return value.toLowerCase() === 'true';
      return false;
    }),
  disableSmsfCC: z
    .union([z.string(), z.boolean()])
    .nullish()
    .transform((value) => {
      if (typeof value === 'boolean') return value;
      if (typeof value === 'string') return value.toLowerCase() === 'true';
      return false;
    }),
});

export type Agent = z.infer<typeof agentSchema>;

export const generalDataSchema = z.object({
  asicMemberId: z.string().default(''),
  /** ASIC splits a given name across three fields; a director with one given
   *  name has two empty strings here, not missing keys. */
  name1: z.string().default(''),
  name2: z.string().default(''),
  name3: z.string().default(''),
  familyName: z.string().default(''),
  email: z.string().default(''),
  phoneNumber: z.string().default(''),
  /** The Director Identification Number. Self-reported, and the one field on
   *  this record the director can actually change. */
  directorId: z.string().nullish(),
  /** ASIC records an address per role, so the same address arrives several
   *  times over; `selectUniqueAddresses` is what collapses them. */
  addresses: z.array(directorAddressSchema).default([]),
  /** True when the "director" is itself a corporate entity, in which case the
   *  name fields hold a company name rather than a person's. */
  isCompany: z.boolean().default(false),
  /** The agents managing this director's companies. The legacy portal read
   *  `agents[0]` for branding and searched the whole array for the payment
   *  flags, so a director can genuinely have several. */
  agents: z.array(agentSchema).default([]),
});

export type GeneralData = z.infer<typeof generalDataSchema>;

/**
 * `POST /director/short-url-login`.
 *
 * A short link (`/u/:token`) carries an opaque id rather than an encoded
 * payload, so the server does the decoding and hands back the same shape a
 * magic link would have carried inline.
 */
export const shortUrlLoginSchema = z.object({
  accessToken: z.string().min(1),
  decodedObject: linkPayloadSchema,
});

export type ShortUrlLogin = z.infer<typeof shortUrlLoginSchema>;

export const otpCodeSchema = z
  .string()
  .trim()
  .regex(/^\d{6}$/, 'Enter the 6-digit code from your message.');
